import {
  createContext,
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import {
  AdaptedEntity,
  CustomSelectedValues,
  GenericEntityType,
  KindEnum,
  Provider,
  TextFieldValue,
} from './types';
import { isEqual, toArray } from 'lodash';
import { PreviewMessage } from '../WbAlert';
import { useAsyncFn } from 'react-use';
import { AsyncState } from 'react-use/lib/useAsyncFn';
import { ProviderFilters } from './GeneralEntityPickerDialog/CommonFilter';

interface GeneralPurposePickerContextType {
  openDialog: boolean;
  setOpenDialog: Dispatch<SetStateAction<boolean>>;
  selectedValues: CustomSelectedValues;
  setSelectedValues: Dispatch<SetStateAction<CustomSelectedValues>>;
  selectedKind: string;
  setSelectedKind: Dispatch<SetStateAction<string>>;
  errorMessage?: PreviewMessage;
  setErrorMessage: React.Dispatch<
    React.SetStateAction<PreviewMessage | undefined>
  >;
  aiState: boolean;
  setAiState: React.Dispatch<React.SetStateAction<boolean>>;
  fetchAiBestMatch: (options: any[]) => Promise<GenericEntityType[]>;
  bestMatchState: AsyncState<GenericEntityType[]>;
  isAiGenerated: (value: GenericEntityType) => boolean;
  formContext?: any;
  selectedEntityProvider?: Provider<GenericEntityType[], ProviderFilters>;
  setSelectedEntityProvider: Dispatch<
    SetStateAction<Provider<GenericEntityType[], ProviderFilters> | undefined>
  >;
  setSuggestedItems: Dispatch<SetStateAction<any[]>>;
  maxItems?: number;
  checkOptionDisabled?: (option: AdaptedEntity) => boolean;
}

export const GeneralPurposePickerContext =
  createContext<GeneralPurposePickerContextType>(
    {} as GeneralPurposePickerContextType,
  );

export interface GeneralPurposePickerProps {
  providerMapper: Map<string, Provider<GenericEntityType[], ProviderFilters>>;
  id?: string;
  availableKinds: Record<string, any>;
  onSelect: (val: TextFieldValue[] | undefined) => void;
  children?: ReactNode;
  value?: TextFieldValue[];
  resolvedValues?: TextFieldValue[];
  templateError?: PreviewMessage;
  fetchAiData?: (data: any[]) => Promise<GenericEntityType[]>;
  formContext?: any;
  maxItems?: number;
  checkOptionDisabled?: (option: AdaptedEntity) => boolean;
}

export const GeneralPurposePickerContextProvider: FC<
  GeneralPurposePickerProps
> = ({
  availableKinds,
  onSelect,
  children,
  resolvedValues,
  templateError,
  fetchAiData,
  formContext,
  providerMapper,
  maxItems,
  checkOptionDisabled,
}: GeneralPurposePickerProps) => {
  const [selectedEntityProvider, setSelectedEntityProvider] =
    useState<Provider<GenericEntityType[], ProviderFilters>>();

  const [aiState, setAiState] = useState<boolean>(false);
  const [suggestedItems, setSuggestedItems] = useState<any[]>([]);

  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<PreviewMessage>();

  const [selectedKind, setSelectedKind] = useState<string>(
    availableKinds[0]?.type,
  );

  useEffect(() => {
    const provider = providerMapper.get(selectedKind);
    if (!provider) {
      setErrorMessage({
        type: 'error',
        title: 'Error',
        content:
          'Error while loading entity provider. Please contact the platform team.',
      });
      return;
    }
    setSelectedEntityProvider(provider);
  }, [providerMapper, selectedKind, setSelectedEntityProvider]);

  const [selectedValues, setSelectedValues] = useState<{
    tableValues: any[];
    arbitraryValues?: any[];
  }>({
    arbitraryValues:
      resolvedValues?.filter(val => val?.kind === KindEnum.arbitrary) ?? [],
    tableValues:
      resolvedValues?.filter(val => val?.kind !== KindEnum.arbitrary) ?? [],
  });

  const [bestMatchState, fetchAiBestMatch] = useAsyncFn(
    async (options: any[]) => {
      try {
        if (!options || !fetchAiData) return [];
        const result = await fetchAiData(options);
        setSuggestedItems(result);

        return result;
      } catch (error: any) {
        setErrorMessage({
          type: 'error',
          title: 'Error on AI Match',
          content: error.message,
        });
        return [];
      }
    },
    [selectedEntityProvider, selectedKind, fetchAiData],
  );

  const isAiGenerated = useCallback(
    (value: GenericEntityType) => {
      return (
        aiState &&
        suggestedItems.some(i => {
          // TODO for now extractEntityId is optional on the provider and we check if implemented and fallback to isEqual, ideally it should be mandatory
          if (selectedEntityProvider?.extractEntityId) {
            const idSuggestion = selectedEntityProvider.extractEntityId(i);
            const idVal = selectedEntityProvider.extractEntityId(value);

            if (idSuggestion !== undefined && idVal !== undefined)
              return idSuggestion === idVal;
          }
          return isEqual(i, value);
        })
      );
    },
    [suggestedItems, aiState, selectedEntityProvider],
  );

  useEffect(() => {
    if (templateError) {
      setErrorMessage({
        type: 'error',
        title: templateError.title,
        content: templateError.content,
      });
    }
  }, [templateError]);

  useEffect(() => {
    setSelectedValues({
      arbitraryValues:
        resolvedValues?.filter(val => val?.kind === KindEnum.arbitrary) ?? [],
      tableValues:
        resolvedValues?.filter(val => val?.kind !== KindEnum.arbitrary) ?? [],
    });
  }, [resolvedValues]);

  useEffect(() => {
    if (!openDialog) {
      setAiState(false);
      setSuggestedItems([]);
    }
  }, [openDialog]);

  useEffect(() => {
    const values = toArray(selectedValues).flat();
    onSelect(values.length ? values : undefined);
  }, [onSelect, selectedValues]);

  return (
    <GeneralPurposePickerContext.Provider
      value={{
        openDialog,
        setOpenDialog,
        selectedValues,
        setSelectedValues,
        selectedKind,
        setSelectedKind,
        errorMessage,
        setErrorMessage,
        aiState,
        setAiState,
        bestMatchState,
        fetchAiBestMatch,
        isAiGenerated,
        formContext,
        selectedEntityProvider,
        setSelectedEntityProvider,
        setSuggestedItems,
        maxItems,
        checkOptionDisabled,
      }}
    >
      {children}
    </GeneralPurposePickerContext.Provider>
  );
};

export const useGeneralPurposePickerContext = () =>
  useContext(GeneralPurposePickerContext);
